import { motion } from 'framer-motion';


// Shake animation for wrong answers
const shakeVariants = {
  shake: {
    x: [0, -8, 8, -6, 6, -3, 3, 0],
    transition: { duration: 0.5 },
  },
};

// Pop animation for correct answers
const popVariants = {
  pop: {
    scale: [1, 1.06, 1],
    transition: { duration: 0.35, ease: 'easeOut' },
  },
};

interface ScenarioQuestionProps {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  onAnswer: (index: number) => void;
  selectedAnswer: number | null;
  isCorrect: boolean | null;
}

export function ScenarioQuestion({
  question,
  options,
  correctIndex,
  explanation,
  onAnswer,
  selectedAnswer,
  isCorrect,
}: ScenarioQuestionProps) {
  // NOTE: options arrive already shuffled from the parent, and `correctIndex`
  // refers to that same order. Re-shuffling here would desync the index the
  // parent uses for grading, marking correct answers as wrong.
  const shuffledOptions = options;
  const shuffledCorrectIndex = correctIndex;

  const answered = selectedAnswer !== null;


  return (
    <div className="w-full space-y-5">
      <div className="text-center">
        <span className="inline-block bg-accent/20 text-accent-foreground font-display font-bold text-xs px-3 py-1 rounded-full mb-3">
          🎬 WHAT HAPPENS NEXT?
        </span>
      </div>

      {/* Scenario card */}
      <div className="bg-card rounded-2xl border-2 border-accent/30 p-5 shadow-card">
        <p className="font-body font-semibold text-base leading-relaxed text-center">
          {question}
        </p>
      </div>

      {/* Answer options */}
      {/*
        CRITICAL: Answer options must never be reshuffled here — always use the
        order passed from the parent component. Reshuffling at this level will
        break the correct answer index and cause correct answers to be marked wrong.
      */}
      <div className="space-y-2.5">
        {shuffledOptions.map((option, i) => {
          const isSelected = i === selectedAnswer;
          const isCorrectOption = i === shuffledCorrectIndex;

          let variant = 'bg-card border-border hover:border-accent/50';
          if (answered) {
            if (isCorrectOption) {
              variant = 'bg-success/10 border-success ring-2 ring-success/30';
            } else if (isSelected && !isCorrect) {
              variant = 'bg-destructive/10 border-destructive ring-2 ring-destructive/30';
            } else {
              variant = 'bg-muted/50 border-border opacity-50';
            }
          }

          const animateState = answered
            ? isSelected && isCorrect
              ? 'pop'
              : isSelected && !isCorrect
                ? 'shake'
                : undefined
            : undefined;

          return (
            <motion.button
              key={i}
              onClick={() => {
                if (!answered) {
                  onAnswer(i);
                }
              }}
              disabled={answered}
              whileTap={!answered ? { scale: 0.98 } : {}}
              variants={{ ...shakeVariants, ...popVariants }}
              animate={animateState}
              className={`w-full p-4 rounded-xl border-2 text-left font-body font-medium transition-colors ${variant}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-sm text-muted-foreground">
                  {String.fromCharCode(65 + i)}.
                </span>
                <span className="flex-1">{option}</span>
                {answered && isCorrectOption && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }}
                    className="text-success text-lg"
                  >
                    ✅
                  </motion.span>
                )}
                {answered && isSelected && !isCorrect && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }}
                    className="text-destructive text-lg"
                  >
                    ❌
                  </motion.span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
